"use client";
import { create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";
import apiClient from "@services/api/apiClient";

export function createRESTStore(config) {
  const {
    entityName = 'data', // Name of the entity (e.g., 'users', 'groups')
    apiPaths, // Object containing API paths for CRUD operations
    initialState, // Initial state of the store (e.g., [], {}, null)
    processResponse, // Object containing functions to process the API response (customizable per storea and per operation)
    additionalActions = () => ({}), // Additional custom actions specific to the store
    persistConfig = null, // Optional persistence configuration
  } = config;

  const store = (set, get, api) => ({
    [entityName]: initialState,
    apiPaths, // Store the initial API paths
    fetching: false,
    hasFetched: false, // becomes true after the first fetchItems resolves
    updating: false,
    creating: false,
    deleting: false,
    fetchingItem: false,

    setApiPaths: (newApiPaths) => {
      set((state) => ({
        ...state,
        apiPaths: newApiPaths,
      }));
    },

    fetchItems: async (query = "") => {
      set({ fetching: true });
      // Using apiClient for authenticated requests
      try {
        const { data: result } = await apiClient(get().apiPaths.fetch + query);
        const processedData =
          processResponse?.fetch
            ? processResponse.fetch(result)
            : result;
        set({ [entityName]: processedData, fetching: false, hasFetched: true });
      } catch (error) {
        set({ fetching: false, hasFetched: true });
        console.log(error);
        // Optionally set a fallback state here
      }
    },

    createItem: async (item) => {
      if (!get().apiPaths.create) return null;
      set({ creating: true });
      // Using apiClient for authenticated requests
      try {
        const { data: result } = await apiClient(get().apiPaths.create, {
          method: "POST",
          body: JSON.stringify(item),
        });
        const processedData =
          processResponse?.create
            ? processResponse.create(result)
            : [...get()[entityName], result];
        set({ [entityName]: processedData, creating: false });
        // Return the created item
        return result;
      } catch (error) {
        set({ creating: false });
        console.log(error);
        return null;
      }
    },

    updateItem: async (id, updates) => {
      if (!get().apiPaths.update) return;
      set({ updating: true });
      // Using apiClient for authenticated requests
      try {
        const response = await apiClient(`${get().apiPaths.update}/${id}`, {
          method: "PUT",
          body: JSON.stringify(updates),
        });
        const updatedItem = await response.json();
        const processedData =
          processResponse?.update
            ? processResponse.update(updatedItem)
            : get()[entityName].map((item) =>
                item.id === id ? updatedItem : item
              );
        set({ [entityName]: processedData, updating: false });
      } catch (error) {
        set({ updating: false });
        console.log(error);
      }
    },

    deleteItem: async (id) => {
      if (!get().apiPaths.delete) return;
      set({ deleting: true });
      // Using apiClient for authenticated requests
      try {
        const response = await apiClient(`${get().apiPaths.delete}/${id}`, {
          method: "DELETE",
        });
        const result = await response.json();
        const processedData =
          processResponse?.delete
            ? processResponse.delete(result)
            : get()[entityName].filter((result) => result.id !== id);
        set({ [entityName]: processedData, deleting: false });
      } catch (error) {
        set({ deleting: false });
        console.log(error);
      }
    },

    getItem: async (id) => {
      if (!get().apiPaths.get) return null;
      set({ fetchingItem: true });
      try {
        const { data: item } = await apiClient(`${get().apiPaths.get}/${id}`);
        set({ fetchingItem: false });
        return processResponse?.getItem
          ? processResponse.getItem(item)
          : item;
      } catch (error) {
        set({ fetchingItem: false });
        if (error.status === 404) {
          console.log(`Error: Item not found (404)`);
        } else {
          console.log("Error fetching item:", error);
        }
        return null;
      }
    },

    ...additionalActions(set, get, api),
  });

  const persistedStore = persistConfig
    ? persist(store, {
        name: persistConfig.name || `${entityName}-store`, // storage name
        partialize: persistConfig.select || ((state) => state), // select which state to persist
        storage: createJSONStorage(
          () => persistConfig.storage || sessionStorage
        ), // specify the storage to use (localStorage, sessionStorage, etc.)
        skipHydration: persistConfig.skipHydration || false, // skip hydration if the storage is empty
      })
    : store;

  return create(devtools(persistedStore));
}
